import { useMutation } from "@apollo/client/react";
import { getUnixTime } from "date-fns/esm";

import FlashContext from "components/FlashMessages/FlashContext";
import useTypedContext from "hooks/useTypedContext";
import useDownloadApiKeyConfig from "shared/ApiKey/useDownloadApiKeyConfig";
import { ApiKey, ApiKeyType } from "types/generated";

import { CREATE_API_KEY } from "./gql";
import { CreateApiKeyFields } from "./types";

type UseCreateApiKeyOptions = {
  callback?: (id: string) => void;
};

const useCreateApiKey = ({ callback }: UseCreateApiKeyOptions) => {
  const { onError, reportSuccess } = useTypedContext(FlashContext);
  const downloadApiKeyConfig = useDownloadApiKeyConfig();

  const [createApiKey, { loading }] = useMutation<{ apiKeyCreate: ApiKey }>(CREATE_API_KEY);

  const onCreate = (formFields: CreateApiKeyFields) => {
    const input = {
      admin: false,
      accessRules: [],
      machine: null,
      name: formFields.name.trim(),
      teams: formFields.groups?.map((group) => group.value) || [],
      type: formFields.type,
      oidc:
        formFields.type === ApiKeyType.Oidc
          ? {
              clientId: formFields.oidcClientId,
              issuer: formFields.oidcIssuer,
              subjectExpression: formFields.oidcSubjectExpression.trim(),
              claimMappings: (() => {
                const filtered = formFields.claimMapping.filter(
                  (claim) => claim.name.trim() && claim.value.trim()
                );
                return filtered.length > 0 ? { entries: filtered } : null;
              })(),
            }
          : null,
      description: formFields.description.trim(),
      roleBindings: formFields.assignedRoles,
      expiresAt: formFields.expiresAt ? getUnixTime(formFields.expiresAt) : null,
    };

    createApiKey({
      variables: {
        input,
      },
    })
      .then(({ data }) => {
        if (data) {
          const {
            apiKeyCreate: { name, id, type, secret },
          } = data;

          if (type === ApiKeyType.Secret) {
            if (!downloadApiKeyConfig({ id, name, secret })) {
              return;
            }
          } else if (type === ApiKeyType.Oidc) {
            reportSuccess({
              message: `API key successfully created`,
            });
          }
          callback?.(id);
        }
      })
      .catch(onError);
  };

  return { onCreate, loading };
};

export default useCreateApiKey;
